import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import Button from '../../components/ui/Button';
import authService from '../../services/authService';
import logoFull from '../../assets/images/logo_full.png';
import logoIcon from '../../assets/images/logo_icon.png';

/**
 * Login Page - Clean Split Screen Design
 * Inspired by compose.ly style with colored left panel
 */

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sessionCode, setSessionCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSessionCodeSubmit = (e) => {
    e.preventDefault();
    if (sessionCode.trim()) {
      navigate(`/join/${sessionCode.trim().toUpperCase()}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const { user } = await authService.login(formData.email, formData.password);

      switch (user.role) {
        case 'super-admin':
          navigate('/admin/dashboard');
          break;
        case 'client':
          navigate('/client/dashboard');
          break;
        case 'trainer':
          navigate('/trainer/dashboard');
          break;
        case 'participant':
          navigate('/participant/dashboard');
          break;
        default:
          navigate('/');
      }
    } catch (error) {
      setErrors({ submit: error.message || 'Invalid email or password. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Left Side - Fixed Colored Panel */}
      <div className="hidden lg:flex lg:w-[45%] bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 p-10 flex-col justify-between fixed top-0 left-0 h-screen overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/20 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary-500/20 rounded-full translate-y-1/2 -translate-x-1/2"></div>

        <div className="relative z-10 flex flex-col h-full justify-between">
          {/* Logo */}
          <Link to="/login" className="flex items-center gap-1 group">
            <img src={logoIcon} alt="Aktivity" className="h-9 w-9 object-contain group-hover:scale-105 transition-transform" />
            <img src={logoFull} alt="Aktivity" className="h-9 object-contain group-hover:scale-105 transition-transform" />
          </Link>

          {/* Participant Join Section */}
          <div className="max-w-md">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Joining as a participant?</h1>
                <p className="text-primary-200 text-sm">Enter your session code to join</p>
              </div>
            </div>

            <p className="text-primary-100 text-base mb-8">
              Enter the session code provided by your trainer to participate in the interactive training session.
            </p>

            {/* Session Code Form */}
            <form onSubmit={handleSessionCodeSubmit} className="space-y-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-neutral-400 font-medium">#</span>
                </div>
                <input
                  type="text"
                  value={sessionCode}
                  onChange={(e) => setSessionCode(e.target.value.toUpperCase())}
                  placeholder="Enter code here"
                  className="w-full pl-10 pr-4 py-4 bg-white border-2 border-white rounded-xl focus:outline-none focus:ring-4 focus:ring-white/30 text-neutral-900 placeholder-neutral-400 font-medium text-lg tracking-wider uppercase"
                  maxLength={10}
                />
              </div>
              <button
                type="submit"
                disabled={!sessionCode.trim()}
                className="w-full py-4 bg-black rounded-xl font-bold text-lg hover:bg-neutral-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-white"
              >
                <span className="text-white">Join Session</span>
                <svg className="w-5 h-5 text-white" fill="none" stroke="white" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </form>

            {/* Help Text */}
            <p className="text-primary-200 text-sm mt-6">
              Don't have a code? Ask your trainer or session organizer for the session code.
            </p>
          </div>

          {/* Footer */}
          <div className="text-primary-200 text-sm">
            Powered by Aktivity - Interactive Training Platform
          </div>
        </div>
      </div>

      {/* Right Side - Login Form (Scrollable) */}
      <div className="w-full lg:w-[55%] lg:ml-[45%] min-h-screen flex flex-col bg-white">
        {/* Top Navigation */}
        <div className="flex justify-between items-start p-5 lg:p-8">
          {/* Mobile Logo */}
          <Link to="/login" className="lg:hidden flex items-center flex-shrink-0">
            <img src={logoIcon} alt="Logo" className="h-7 w-7 object-contain" />
            <img src={logoFull} alt="Aktivity" className="h-7 object-contain ml-1" />
          </Link>
          <div className="hidden lg:block"></div>

          <p className="text-xs sm:text-sm text-neutral-600 text-right flex-shrink-0 ml-4">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary-600 hover:text-primary-700 font-semibold whitespace-nowrap">
              Sign up
            </Link>
          </p>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center px-6 lg:px-16 pb-8 lg:pb-12">
          <div className="w-full max-w-md">
            {/* Form Header */}
            <div className="mb-8">
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-neutral-900 mb-2">Sign in to your account</h2>
              <p className="text-xs sm:text-sm text-neutral-500">Enter your credentials to access your dashboard</p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-neutral-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@company.com"
                  required
                  className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white transition-all text-neutral-900 placeholder-neutral-400"
                />
              </div>

              {/* Password Input */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-neutral-700">
                    Password
                  </label>
                  <Link to="/forgot-password" state={{ email: formData.email }} className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 font-medium">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    required
                    className="w-full px-4 py-3 pr-11 bg-neutral-50 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white transition-all text-neutral-900 placeholder-neutral-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-xs sm:text-sm">
                  {errors.submit}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
                className="mt-2"
              >
                <span className="flex items-center justify-center gap-2">
                  {loading ? 'Signing in...' : 'Sign In'}
                  {!loading && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  )}
                </span>
              </Button>
            </form>

            {/* Mobile Participant Join Section - Below Login */}
            <div className="lg:hidden mt-8">
              <div className="relative">
                {/* Divider */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="flex-1 h-px bg-neutral-200"></div>
                  <span className="text-xs text-neutral-400 font-medium uppercase tracking-wider">or</span>
                  <div className="flex-1 h-px bg-neutral-200"></div>
                </div>

                <div className="border border-neutral-200 rounded-xl p-4">
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xs sm:text-sm font-semibold text-neutral-900">Join as a participant</h3>
                      <p className="text-xs text-neutral-500">Enter your session code</p>
                    </div>
                  </div>

                  <form onSubmit={handleSessionCodeSubmit} className="flex gap-2">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-neutral-400 font-medium text-sm">#</span>
                      </div>
                      <input
                        type="text"
                        value={sessionCode}
                        onChange={(e) => setSessionCode(e.target.value.toUpperCase())}
                        placeholder="ENTER CODE"
                        className="w-full pl-8 pr-3 py-2.5 bg-neutral-50 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white text-neutral-900 placeholder-neutral-400 font-medium text-sm tracking-wider uppercase transition-all"
                        maxLength={10}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={!sessionCode.trim()}
                      className="px-4 py-2.5 bg-primary-600 rounded-lg font-semibold text-sm hover:bg-primary-700 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 text-white flex-shrink-0"
                    >
                      Join
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
